import { Component } from '@angular/core';
import { ApplicationFormService } from '../../service/application-form.service';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CaseManagementService } from '../../service/case-management.service';
import { catchError, forkJoin, map, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SharedCaseManagementService } from '../../shared/services/shared-case-management.service';
import { DateTime } from 'luxon';
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS , MAT_LUXON_DATE_FORMATS } from '@angular/material-luxon-adapter';

@Component({
  selector: 'app-preview-application-form',
  standalone: true,
  imports: [MatCardModule,CommonModule, MatDatepickerModule, ReactiveFormsModule, MatDatepickerModule, MatFormFieldModule,MatInputModule, MatProgressSpinnerModule, CommonModule, MatButtonModule, MatTooltipModule ],
  providers: [
    { provide: DateAdapter, useClass: LuxonDateAdapter, deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
  ],
  templateUrl: './preview-application-form.component.html',
  styleUrl: './preview-application-form.component.scss'
})
export class PreviewApplicationFormComponent {
  constructor(private readonly applicationFormService:ApplicationFormService,
    private readonly caseManagementService: CaseManagementService,
    private readonly toastr: ToastrService,
    private readonly sharedCaseManagementService: SharedCaseManagementService
  ) { }
  applicationDetails: any;
  applicationUiFormAsJson: any;
  subForms: any;
  applicationForm: any;
  fields: any;
  sel:any = [];
  isLoading = true;
  documentsList: any;
  caseId: any = '';
  formLength: any;
  ngOnInit() {
    this.isLoading = true;
    this.caseId = localStorage.getItem('caseId');
    this.sharedCaseManagementService.changeBellIconVisibility(false);
    this.getAllData();
  }
  getAllData() {
    forkJoin(
      [
        this.applicationFormService.getSubFormFormData(this.caseId),
        this.caseManagementService.getAllDocuments(this.caseId),
      ])
    .pipe(
      map(([applicationFormDetails, documentDetails]) => {
        return { applicationFormDetails, documentDetails };
      }),
      catchError((error) => of({ error }))
    )
    .subscribe({
      next: (data) => {
        this.isLoading = false;
        if ('applicationFormDetails' in data) {
          this.applicationDetails = data.applicationFormDetails;
          this.applicationUiFormAsJson = data.applicationFormDetails?.applicationUiFormAsJson;
          this.subForms = this.applicationUiFormAsJson.subforms;
          this.formLength = this.subForms?.length;

          let subformBasicInfo = data?.applicationFormDetails?.applicationUiFormAsJson.subforms[0]?.subform_sections.findIndex((subForm:any)=>subForm.section_name === 'Basic Info');
          this.subForms[0]?.subform_sections[subformBasicInfo]?.fields?.forEach((field: any) => {
            if(field.control_name == 'DateOfBirth') {
              field.selected_values = [DateTime.fromISO(field.selected_values[0]).toFormat('MM/dd/yyyy')];
            }
          });
          let secondLastSubFormIndex = this.formLength - 2;
          let tafCreationIndex = -1;
          data?.applicationFormDetails?.applicationUiFormAsJson?.subforms?.forEach((subForm: any) => {
            if(tafCreationIndex !== -1) {
              return;
            }
            else {
              tafCreationIndex = subForm.subform_sections?.findIndex((section: any) => section.section_name === 'TAF Creation');
            }
          });

          this.subForms[secondLastSubFormIndex]?.subform_sections[tafCreationIndex]?.fields.forEach((field: any) => {
            if(field.control_name == 'createdBy') {
              field.selected_values = [this.applicationDetails?.createdName];
            }
            if(field.control_name == 'dateCreated') {
              field.selected_values = [DateTime.fromJSDate(new Date(this.applicationDetails.createdDate)).toUTC().toFormat('yyyy-MM-dd')];
            }
            if(field.control_name == 'modifiedBy') {
              field.selected_values = [this.applicationDetails?.updatedName];
            }
            if(field.control_name == 'dateModified') {
              field.selected_values = [DateTime.fromJSDate(new Date(this.applicationDetails.updatedDate)).toUTC().toFormat('yyyy-MM-dd')];
            }
          });
        }
        else {
          this.toastr.error('Error fetching application form details');
          this.isLoading = false;
        }
        if ('documentDetails' in data) {
          this.documentsList = data.documentDetails;
          
          this.documentsList.forEach((document: any) => {
            document.createdDate = DateTime.fromISO(document.createdDate).toFormat('MM/dd/yyyy');
          });
        }
        else {
          this.toastr.error('Error fetching document details');
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('There was an error fetching the data:', error);
        this.toastr.error('There was an error fetching the data. Please try again.');
        this.isLoading = false;
      }
    });
  }
  viewDocument(documentObj: any) {
    this.caseManagementService.viewDocument(this.caseId, documentObj.id).subscribe({
      next: (response: Blob) => {
        const fileURL = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = fileURL;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileURL);
      },
      error: (error) => {
        console.error('Error viewing document:', error);
      }
    });
  }
  downloadDocument(documentObj: any): void {
    this.caseManagementService.downloadDocument(this.caseId, documentObj.id).subscribe({
      next: (response: Blob) => {
        let documentType = documentObj.path?.substring(documentObj.path?.lastIndexOf('.') + 1);
        
        // Creating a temporary link to trigger the download
        const fileURL = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = documentObj?.name + '.'+ documentType;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(fileURL);
        this.toastr.success(`Document download started ...`, '', {
          timeOut: 1000,
        });
      },
      error: (error) => {
        console.error('Error downloading document:', error);
        this.toastr.error('Could not download the document', '', { timeOut: 1000 });
      }
    });
  }
  closeTab() {
    this.sharedCaseManagementService.changeBellIconVisibility(true);
    window.close();
  }
}
